// The flat's sellers badge (mandate plan 2.C.3): one phrase per flat,
// "Sahibi və 3 vasitəçi", from who has offered it across every posting.
// The keys are written out in full so the i18n route scan ships them
// with every page that renders a card (scripts/i18n-scan.mjs).
import type { TranslationKey } from "@/lib/i18n/keys";
import type { FlatSellers } from "@/types";

type Form = "one" | "few" | "many";

const OWNER_AGENTS: Record<Form, TranslationKey> = {
  one: "listings.flat_sellers.owner_agents.one",
  few: "listings.flat_sellers.owner_agents.few",
  many: "listings.flat_sellers.owner_agents.many",
};
const AGENTS: Record<Form, TranslationKey> = {
  one: "listings.flat_sellers.agents.one",
  few: "listings.flat_sellers.agents.few",
  many: "listings.flat_sellers.agents.many",
};

/** ru has three count forms (1 посредник, 2-4 посредника, 5+ посредников);
 *  az and en read "one" / "many" (their "few" says what "many" says). */
function pluralForm(n: number, locale: string): Form {
  if (locale === "ru") {
    const m10 = n % 10;
    const m100 = n % 100;
    if (m10 === 1 && m100 !== 11) return "one";
    if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return "few";
    return "many";
  }
  return n === 1 ? "one" : "many";
}

/** True when the badge says something: the owner among the sellers or at
 *  least one agent. A lone posting arrives as null and shows nothing. */
export function hasFlatSellers(fs: FlatSellers | null | undefined): fs is FlatSellers {
  return !!fs && (fs.owner === true || (fs.agents ?? 0) > 0);
}

/** The key and count of the phrase; null when there is nothing to say.
 *  The owner alone reads by the deal ("Sahibi satır" / "Sahibi kirayə
 *  verir"); with agents the deal does not change the words. */
export function flatSellersKey(
  fs: FlatSellers | null | undefined,
  leased: boolean | null | undefined,
  locale: string,
): { key: TranslationKey; n: number } | null {
  if (!hasFlatSellers(fs)) return null;
  const n = Math.max(0, Math.round(fs.agents ?? 0));
  if (fs.owner && n === 0) {
    return { key: leased ? "listings.flat_sellers.owner_rent" : "listings.flat_sellers.owner_sale", n };
  }
  return { key: (fs.owner ? OWNER_AGENTS : AGENTS)[pluralForm(n, locale)], n };
}

/** The phrase itself, through the caller's translator. */
export function flatSellersText(
  fs: FlatSellers | null | undefined,
  leased: boolean | null | undefined,
  locale: string,
  t: (key: TranslationKey) => string,
): string | null {
  const k = flatSellersKey(fs, leased, locale);
  return k ? t(k.key).replace("{n}", String(k.n)) : null;
}
